import React, { useState, useEffect, useRef } from 'react';
import { translate } from '../Services/translation';

const BLOCK_TYPES = {
    MainNavigationLink: 'MainNavigationLink',
    SecondaryNavigationLink: 'SecondaryNavigationLink',
    MegaMenuColumn: 'MegaMenuColumn',
    MegaMenuSubColumn: 'MegaMenuSubColumn',
    MegaMenuTitleLink: 'MegaMenuTitleLink',
    MegaMenuLinkItem: 'MegaMenuLinkItem',
};

const VIRTUAL_LINK_ROW = '__VirtualLinkRow__';

const flattenStructuralWrappers = (children = []) => {
    const result = [];
    children.forEach((child) => {
        if (child.type === BLOCK_TYPES.MegaMenuSubColumn) {
            const flattened = flattenStructuralWrappers(child.children || []);
            flattened.forEach((sub) => result.push(sub));
            return;
        }
        if (child.type === BLOCK_TYPES.MegaMenuTitleLink && !child.text) {
            const flattened = flattenStructuralWrappers(child.children || []);
            flattened.forEach((sub) => result.push(sub));
            return;
        }
        if (child.type === BLOCK_TYPES.MegaMenuColumn) {
            const hasHeading = !!child.heading;
            if (!hasHeading) {
                const subChildren = flattenStructuralWrappers(child.children || []);
                subChildren.forEach((sub) => result.push(sub));
                return;
            }
            if (child.mobileMode === 'Flat') {
                const extraIndent = child.flattenChildrenInMobile ? 0 : 16;
                const subChildren = flattenStructuralWrappers(child.children || [])
                    .map((c) => {
                        const next = { ...c, _flatStyle: true };
                        if (extraIndent > 0) {
                            next._extraIndent = (next._extraIndent || 0) + extraIndent;
                        }
                        return next;
                    });
                result.push({
                    type: VIRTUAL_LINK_ROW,
                    text: child.heading,
                    url: child.url,
                    isExternal: child.isExternal,
                    fontWeightClass: 'tw-font-semibold',
                });
                subChildren.forEach((sub) => result.push(sub));
                return;
            }
            result.push(child);
            return;
        }
        result.push(child);
    });
    return result;
};

const CaretIcon = ({ open }) => (
    <img
        src="/ui/images/chevron-down.svg"
        alt=""
        width="20"
        height="20"
        aria-hidden="true"
        className={`tw-block tw-transition-transform tw-duration-200 ${open ? 'tw-rotate-180' : ''}`}
    />
);

const LinkRow = ({ item, indent = 0, onNavigate, fontWeightClass = 'tw-font-normal', paddingLeft = 16, rowHeight = 48, showBorder = true }) => {
    const borderClass = indent === 0 && showBorder ? 'main-nav__divider-bottom' : '';
    return (
        <a
            href={item.url || '#'}
            onClick={onNavigate}
            className={`tw-flex tw-items-center tw-w-full tw-text-lg tw-leading-tight tw-text-black ${fontWeightClass} ${borderClass}`}
            style={{ paddingLeft: `${paddingLeft}px`, paddingRight: '16px', height: `${rowHeight}px` }}
        >
            {item.text}
        </a>
    );
};

const ExpandableRow = ({ item, children, level = 0, isOpen, onToggle, onNavigate, fontWeightClass = 'tw-font-bold', paddingLeft = 16, rowHeight = 48 }) => {
    const indent = paddingLeft;
    const expandable = children.length > 0;
    const lastChildIsColumn = expandable && children.length > 0 &&
        children[children.length - 1].type === BLOCK_TYPES.MegaMenuColumn;

    const headerContent = (
        <span className={`tw-flex-1 tw-text-lg tw-leading-tight tw-text-black ${fontWeightClass}`}>
            {item.heading || item.text}
        </span>
    );

    const toggle = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
    };

    const headerBorderClass = level === 0 && !isOpen ? 'main-nav__divider-bottom' : '';
    const wrapperBorderClass = level === 0 && isOpen ? 'main-nav__divider-bottom' : '';
    const wrapperBorderLeftClass = level === 0 && isOpen ? 'main-nav__open-indicator' : '';
    const childrenBottomPadClass = level === 0 && lastChildIsColumn ? 'tw-pb-2' : '';

    return (
        <div className={`tw-flex tw-flex-col ${wrapperBorderClass} ${wrapperBorderLeftClass}`}>
            <div
                className={`tw-flex tw-items-center tw-w-full tw-cursor-pointer ${headerBorderClass}`}
                style={{ paddingLeft: `${indent}px`, height: `${rowHeight}px` }}
                onClick={expandable ? toggle : undefined}
            >
                {headerContent}
                {expandable ? (
                    <button
                        type="button"
                        onClick={toggle}
                        className="tw-flex tw-items-center tw-justify-center tw-w-12 tw-h-12 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0 tw-text-black"
                        aria-label={`${isOpen ? 'Stäng' : 'Öppna'} ${item.heading || item.text}`}
                        aria-expanded={isOpen}
                    >
                        <CaretIcon open={isOpen} />
                    </button>
                ) : null}
            </div>
            {expandable && isOpen ? (
                <div className={`tw-flex tw-flex-col ${childrenBottomPadClass}`}>
                    <SiblingGroup items={children} level={level + 1} onNavigate={onNavigate} />
                </div>
            ) : null}
        </div>
    );
};

const SiblingGroup = ({ items, level = 0, onNavigate }) => {
    const [openIndex, setOpenIndex] = useState(-1);

    return (
        <>
            {items.map((child, idx) => (
                <MenuNode
                    key={idx}
                    item={child}
                    level={level}
                    isOpen={openIndex === idx}
                    onToggle={() => setOpenIndex(openIndex === idx ? -1 : idx)}
                    onNavigate={onNavigate}
                />
            ))}
        </>
    );
};

const MenuNode = ({ item, level = 0, isOpen, onToggle, onNavigate }) => {
    const type = item.type;
    const rawChildren = (item.children || []).map((c) => {
        let next = c;
        if (item._flatStyle) next = { ...next, _flatStyle: true };
        if (item._extraIndent) next = { ...next, _extraIndent: (next._extraIndent || 0) + item._extraIndent };
        return next;
    });
    const flatChildren = flattenStructuralWrappers(rawChildren);

    if (type === BLOCK_TYPES.MainNavigationLink) {
        if (flatChildren.length === 0) {
            return (
                <LinkRow
                    item={item}
                    indent={0}
                    onNavigate={onNavigate}
                    fontWeightClass="tw-font-bold"
                    paddingLeft={16}
                    rowHeight={48}
                />
            );
        }
        return (
            <ExpandableRow
                item={item}
                children={flatChildren}
                level={level}
                isOpen={isOpen}
                onToggle={onToggle}
                onNavigate={onNavigate}
                fontWeightClass="tw-font-bold"
            />
        );
    }

    if (type === BLOCK_TYPES.MegaMenuColumn) {
        const titleText = item.heading || item.text;
        const isFlatChildren = item.mobileMode === 'AccordionFlatChildren';
        const columnRowHeight = item.narrowHeadingInMobile ? 40 : 48;
        const extraIndent = item.flattenChildrenInMobile ? 0 : 16;
        if (flatChildren.length > 0) {
            let children = flatChildren;
            if (isFlatChildren) {
                children = children.map((c) => ({ ...c, _flatStyle: true }));
            }
            if (extraIndent > 0) {
                children = children.map((c) => ({ ...c, _extraIndent: (c._extraIndent || 0) + extraIndent }));
            }
            if (item.showAllInMobile && item.url) {
                const allPrefixRaw = translate('mobileMenu.allPrefix');
                const allPrefix = allPrefixRaw === 'mobileMenu.allPrefix' ? 'Alla ' : allPrefixRaw;
                children = [
                    {
                        type: VIRTUAL_LINK_ROW,
                        text: `${allPrefix}${titleText}`,
                        url: item.url,
                        isExternal: item.isExternal,
                        fontWeightClass: isFlatChildren ? 'tw-font-normal' : 'tw-font-medium',
                        indentOverride: (isFlatChildren ? 16 : 32) + extraIndent,
                    },
                    ...children,
                ];
            }
            return (
                <ExpandableRow
                    item={{ ...item, text: titleText }}
                    children={children}
                    level={level}
                    isOpen={isOpen}
                    onToggle={onToggle}
                    onNavigate={onNavigate}
                    fontWeightClass="tw-font-semibold"
                    paddingLeft={16}
                    rowHeight={columnRowHeight}
                />
            );
        }
        if (titleText) {
            return (
                <LinkRow
                    item={{ ...item, text: titleText }}
                    indent={0}
                    onNavigate={onNavigate}
                    fontWeightClass="tw-font-semibold"
                    paddingLeft={16}
                    rowHeight={columnRowHeight}
                    showBorder={false}
                />
            );
        }
        return null;
    }

    if (type === VIRTUAL_LINK_ROW) {
        const pl = (item.indentOverride || 16) + (item._extraIndent || 0);
        const rh = 40;
        return (
            <LinkRow
                item={item}
                indent={0}
                onNavigate={onNavigate}
                fontWeightClass={item.fontWeightClass || 'tw-font-semibold'}
                paddingLeft={pl}
                rowHeight={rh}
                showBorder={false}
            />
        );
    }

    if (type === BLOCK_TYPES.MegaMenuTitleLink) {
        const titleFontWeight = isOpen ? 'tw-font-semibold' : 'tw-font-medium';
        const extraIndent = item._extraIndent || 0;
        const titlePadding = 16 + extraIndent;
        const childrenFlat = item.titleLinkMobileMode === 'Flat';
        if (flatChildren.length > 0) {
            const allPrefixRaw = translate('mobileMenu.allPrefix');
            const allPrefix = allPrefixRaw === 'mobileMenu.allPrefix' ? 'Alla ' : allPrefixRaw;
            const childIndent = (childrenFlat ? 16 : 32) + extraIndent;
            const allRow = item.showAllInMobile && item.url ? [{
                type: VIRTUAL_LINK_ROW,
                text: `${allPrefix}${item.text}`,
                url: item.url,
                isExternal: item.isExternal,
                fontWeightClass: 'tw-font-medium',
                indentOverride: childIndent,
            }] : [];
            const markedChildren = flatChildren.map((c) => ({
                ...c,
                _titleFlatChildren: childrenFlat,
                _flatStyle: childrenFlat ? c._flatStyle : false,
                _extraIndent: extraIndent,
            }));
            const childrenWithAll = [...allRow, ...markedChildren];
            return (
                <ExpandableRow
                    item={item}
                    children={childrenWithAll}
                    level={level}
                    isOpen={isOpen}
                    onToggle={onToggle}
                    onNavigate={onNavigate}
                    fontWeightClass={titleFontWeight}
                    paddingLeft={titlePadding}
                    rowHeight={40}
                />
            );
        }
        return (
            <LinkRow
                item={item}
                indent={level}
                onNavigate={onNavigate}
                fontWeightClass="tw-font-medium"
                paddingLeft={titlePadding}
                rowHeight={40}
            />
        );
    }

    if (type === BLOCK_TYPES.MegaMenuLinkItem) {
        const flatStyle = item._flatStyle;
        const titleFlat = item._titleFlatChildren;
        const extraIndent = item._extraIndent || 0;
        const pl = ((flatStyle || titleFlat) ? 16 : 32) + extraIndent;
        return (
            <LinkRow
                item={item}
                indent={level}
                onNavigate={onNavigate}
                paddingLeft={pl}
                rowHeight={40}
                fontWeightClass="tw-font-normal"
            />
        );
    }

    return null;
};

const MobileMenu = ({ data, open, onClose }) => {
    const panelRef = useRef(null);
    const [maxHeight, setMaxHeight] = useState(null);

    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const updateHeight = () => {
            if (!panelRef.current) return;
            const rect = panelRef.current.getBoundingClientRect();
            setMaxHeight(window.innerHeight - rect.top);
        };
        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const previouslyFocused = document.activeElement;

        requestAnimationFrame(() => {
            if (!panelRef.current) return;
            panelRef.current.focus({ preventScroll: true });
        });

        const handleKey = (e) => {
            if (e.key === 'Escape') {
                onClose();
                return;
            }
            if (e.key === 'Tab' && panelRef.current) {
                const focusables = panelRef.current.querySelectorAll(
                    'a[href], button:not([disabled]), input:not([disabled])'
                );
                if (focusables.length === 0) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        window.addEventListener('keydown', handleKey);

        return () => {
            window.removeEventListener('keydown', handleKey);
            if (previouslyFocused && previouslyFocused.focus) {
                previouslyFocused.focus();
            }
        };
    }, [open, onClose]);

    const items = (data && data.items) || [];
    const secondaryItems = (data && data.secondaryItems) || [];
    const topBarRight = (data && data.topBarRightLinks) || [];
    const topBarLeft = (data && data.topBarLeftLinks) || [];
    const topBarLinks = [...topBarRight, ...topBarLeft];

    if (!open) return null;

    return (
        <div
            ref={panelRef}
            id="mobileMenu"
            tabIndex={-1}
            className="tw-w-full tw-bg-white tw-overflow-y-auto main-nav__mobile-panel tw-outline-none"
            style={maxHeight ? { height: `${maxHeight}px` } : undefined}
            role="dialog"
            aria-modal="true"
            aria-label="Mobile menu"
        >
            <SiblingGroup items={items} level={0} onNavigate={onClose} />
            {topBarLinks.length > 0 ? (
                <div className="tw-flex tw-flex-col">
                    {topBarLinks.map((item, idx) => (
                        <a
                            key={`tb-${idx}`}
                            href={item.url || '#'}
                            onClick={onClose}
                            className="tw-flex tw-items-center tw-w-full tw-px-4 tw-py-3 tw-text-lg tw-font-normal tw-leading-tight tw-text-black main-nav__divider-bottom"
                        >
                            {item.text}
                        </a>
                    ))}
                </div>
            ) : null}
            {secondaryItems.length > 0 ? (
                <div className="tw-flex tw-flex-col">
                    {secondaryItems.map((item, idx) => (
                        <a
                            key={`sec-${idx}`}
                            href={item.url || '#'}
                            onClick={onClose}
                            className="tw-flex tw-items-center tw-w-full tw-px-4 tw-py-3 tw-text-lg tw-font-normal tw-leading-tight tw-text-black main-nav__divider-bottom"
                        >
                            {item.text}
                        </a>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

export default MobileMenu;
